import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from 'react-bootstrap';
import PageShell from '../Components/ui/PageShell';
import DashboardCard from '../Components/ui/DashboardCard';
import DataTable from '../Components/ui/DataTable';
import AppModal from '../Components/ui/AppModal';
import RequirementDateRangeFilter from '../Components/ui/RequirementDateRangeFilter';
import { getAdminAnalytics } from '../services/adminAnalyticsService';
import ProfilesSourcedBarChartCard from '../Components/analytics/ProfilesSourcedBarChartCard';
import { aggregateProfilesSourcedCharts } from '../utils/profilesSourcedAggregates';
import { formatDisplayDate } from '../utils/dateFormat';
import { getDefaultDashboardDateRange, matchesDefaultDashboardDateRange } from '../utils/requirementDateFilter';

function getDefaultProfilesWeekRange() {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - 6);
  return {
    from: from.toLocaleDateString('en-CA'),
    to: to.toLocaleDateString('en-CA'),
  };
}

function matchesDefaultProfilesWeekRange(fromDate, toDate) {
  if (!fromDate || !toDate) return false;
  const expected = getDefaultProfilesWeekRange();
  return fromDate === expected.from && toDate === expected.to;
}

function getTodayProfilesRange() {
  const today = new Date().toLocaleDateString('en-CA');
  return { from: today, to: today };
}

function matchesTodayProfilesRange(fromDate, toDate) {
  if (!fromDate || !toDate) return false;
  const expected = getTodayProfilesRange();
  return fromDate === expected.from && toDate === expected.to;
}

function getYesterdayProfilesRange() {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  const date = yesterday.toLocaleDateString('en-CA');
  return { from: date, to: date };
}

function matchesYesterdayProfilesRange(fromDate, toDate) {
  if (!fromDate || !toDate) return false;
  const expected = getYesterdayProfilesRange();
  return fromDate === expected.from && toDate === expected.to;
}

const INITIAL_PROFILES_WEEK_RANGE = getDefaultProfilesWeekRange();
const INITIAL_CARD_STATS_RANGE = getDefaultDashboardDateRange();
const OPEN_REQUIREMENT_TYPES = 'High,Medium,Low';

function buildRequirementsAnalyticsLink(fromDate, toDate, types) {
  const params = new URLSearchParams({
    from: fromDate,
    to: toDate,
    dateField: 'startDate',
    types,
  });
  return `/Requirments?${params.toString()}`;
}

function FunnelChart({ stages = [] }) {
  const max = Math.max(...stages.map((s) => s.count), 1);

  return (
    <div className="hr-funnel-chart">
      {stages.map((stage) => (
        <div key={stage.key} className="hr-funnel-row">
          <span className="hr-funnel-label">{stage.label}</span>
          <div className="hr-funnel-bar-track">
            <div
              className="hr-funnel-bar-fill"
              style={{ width: `${Math.max((stage.count / max) * 100, stage.count > 0 ? 8 : 0)}%` }}
            />
          </div>
          <span className="hr-funnel-value">{stage.count}</span>
        </div>
      ))}
    </div>
  );
}

function AdminAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profilesLoading, setProfilesLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [fromDate, setFromDate] = useState(INITIAL_PROFILES_WEEK_RANGE.from);
  const [toDate, setToDate] = useState(INITIAL_PROFILES_WEEK_RANGE.to);
  const [profilesPeriod, setProfilesPeriod] = useState('week');
  const [cardStatsFrom, setCardStatsFrom] = useState(INITIAL_CARD_STATS_RANGE.from);
  const [cardStatsTo, setCardStatsTo] = useState(INITIAL_CARD_STATS_RANGE.to);
  const [recruiterId, setRecruiterId] = useState('');
  const [teamLeadId, setTeamLeadId] = useState('');
  const [drillDownModal, setDrillDownModal] = useState({
    show: false,
    title: '',
    rows: [],
    mode: 'candidate',
    footerLink: '',
    footerLabel: '',
  });

  const loadAnalytics = useCallback(async (filters = {}, { profilesOnly = false } = {}) => {
    if (profilesOnly) {
      setProfilesLoading(true);
    } else {
      setLoading(true);
      setLoadError('');
    }

    try {
      const response = await getAdminAnalytics(filters);
      if (profilesOnly) {
        setData((prev) => (
          prev
            ? { ...prev, profilesSourcedReport: response.profilesSourcedReport }
            : response
        ));
      } else {
        setData(response);
      }
    } catch (error) {
      console.error('Analytics load failed:', error);
      if (!profilesOnly) {
        setLoadError(error.message || 'Failed to load analytics.');
      }
    } finally {
      if (profilesOnly) {
        setProfilesLoading(false);
      } else {
        setLoading(false);
      }
    }
  }, []);

  const buildAnalyticsFilters = useCallback((overrides = {}) => ({
    from: fromDate,
    to: toDate,
    recruiterId: teamLeadId ? '' : recruiterId,
    teamLeadId,
    statsFrom: cardStatsFrom,
    statsTo: cardStatsTo,
    ...overrides,
  }), [fromDate, toDate, recruiterId, teamLeadId, cardStatsFrom, cardStatsTo]);

  useEffect(() => {
    loadAnalytics({
      from: INITIAL_PROFILES_WEEK_RANGE.from,
      to: INITIAL_PROFILES_WEEK_RANGE.to,
      statsFrom: INITIAL_CARD_STATS_RANGE.from,
      statsTo: INITIAL_CARD_STATS_RANGE.to,
    });
  }, [loadAnalytics]);

  const applyCardStatsFilters = () => {
    loadAnalytics(buildAnalyticsFilters());
  };

  const resetCardStatsFilters = () => {
    const defaultRange = getDefaultDashboardDateRange();
    setCardStatsFrom(defaultRange.from);
    setCardStatsTo(defaultRange.to);
    loadAnalytics(buildAnalyticsFilters({
      statsFrom: defaultRange.from,
      statsTo: defaultRange.to,
    }));
  };

  const applyProfilesFilters = () => {
    loadAnalytics(buildAnalyticsFilters(), { profilesOnly: true });
  };

  const clearProfilesFilters = () => {
    const defaultRange = getDefaultProfilesWeekRange();
    setProfilesPeriod('week');
    setFromDate(defaultRange.from);
    setToDate(defaultRange.to);
    setRecruiterId('');
    setTeamLeadId('');
    loadAnalytics(buildAnalyticsFilters({
      from: defaultRange.from,
      to: defaultRange.to,
    }), { profilesOnly: true });
  };

  const selectProfilesWeek = () => {
    const defaultRange = getDefaultProfilesWeekRange();
    setProfilesPeriod('week');
    setFromDate(defaultRange.from);
    setToDate(defaultRange.to);
    loadAnalytics(buildAnalyticsFilters({
      from: defaultRange.from,
      to: defaultRange.to,
    }), { profilesOnly: true });
  };

  const selectProfilesToday = () => {
    const todayRange = getTodayProfilesRange();
    setProfilesPeriod('today');
    setFromDate(todayRange.from);
    setToDate(todayRange.to);
    loadAnalytics(buildAnalyticsFilters({
      from: todayRange.from,
      to: todayRange.to,
    }), { profilesOnly: true });
  };

  const selectProfilesYesterday = () => {
    const yesterdayRange = getYesterdayProfilesRange();
    setProfilesPeriod('yesterday');
    setFromDate(yesterdayRange.from);
    setToDate(yesterdayRange.to);
    loadAnalytics(buildAnalyticsFilters({
      from: yesterdayRange.from,
      to: yesterdayRange.to,
    }), { profilesOnly: true });
  };

  const profilesReport = data?.profilesSourcedReport;
  const filterOptions = profilesReport?.filterOptions || { teamLeads: [], recruiters: [] };
  const profilesRows = useMemo(
    () => profilesReport?.rows || [],
    [profilesReport?.rows],
  );
  const profilesChartData = useMemo(
    () => aggregateProfilesSourcedCharts(profilesRows),
    [profilesRows]
  );
  const hasProfilesFilters = Boolean(
    profilesReport?.filters?.fromDate
    || profilesReport?.filters?.toDate
    || profilesReport?.filters?.recruiterId
    || profilesReport?.filters?.teamLeadId
  );
  const isDefaultWeekView = Boolean(
    profilesReport
    && !profilesReport.filters?.recruiterId
    && !profilesReport.filters?.teamLeadId
    && matchesDefaultProfilesWeekRange(
      profilesReport.filters?.fromDate,
      profilesReport.filters?.toDate
    )
  );
  const isTodayProfilesView = Boolean(
    profilesReport
    && !profilesReport.filters?.recruiterId
    && !profilesReport.filters?.teamLeadId
    && matchesTodayProfilesRange(
      profilesReport.filters?.fromDate,
      profilesReport.filters?.toDate
    )
  );
  const isYesterdayProfilesView = Boolean(
    profilesReport
    && !profilesReport.filters?.recruiterId
    && !profilesReport.filters?.teamLeadId
    && matchesYesterdayProfilesRange(
      profilesReport.filters?.fromDate,
      profilesReport.filters?.toDate
    )
  );

  const stats = data?.dashboardStats || {};
  const cardStatsPeriod = data?.dashboardStatsPeriod || INITIAL_CARD_STATS_RANGE;
  const pipelinePeriod = data?.pipelineFunnelPeriod || cardStatsPeriod;
  const drillDown = data?.dashboardDrillDown || {};
  const upcomingInterviews = data?.upcomingInterviews || [];
  const interviewCount = upcomingInterviews.length;
  const openRequirementsLink = buildRequirementsAnalyticsLink(
    cardStatsPeriod.fromDate || INITIAL_CARD_STATS_RANGE.from,
    cardStatsPeriod.toDate || INITIAL_CARD_STATS_RANGE.to,
    OPEN_REQUIREMENT_TYPES,
  );
  const onHoldRequirementsLink = buildRequirementsAnalyticsLink(
    cardStatsPeriod.fromDate || INITIAL_CARD_STATS_RANGE.from,
    cardStatsPeriod.toDate || INITIAL_CARD_STATS_RANGE.to,
    'Hold',
  );

  const openDrillDownModal = ({
    title,
    rows,
    mode = 'candidate',
    footerLink = '',
    footerLabel = '',
  }) => {
    setDrillDownModal({
      show: true,
      title,
      rows: Array.isArray(rows) ? rows : [],
      mode,
      footerLink,
      footerLabel,
    });
  };

  const closeDrillDownModal = () => {
    setDrillDownModal({
      show: false,
      title: '',
      rows: [],
      mode: 'candidate',
      footerLink: '',
      footerLabel: '',
    });
  };

  const interviewColumns = useMemo(() => [
    { key: 'candidateName', label: 'Source Profile', sortable: true },
    { key: 'client', label: 'Client', sortable: true },
    { key: 'role', label: 'Role', sortable: true },
    { key: 'status', label: 'Status', sortable: true, type: 'status', useStoredLabel: true },
    { key: 'interviewDateLabel', label: 'Date & Time', sortable: true },
  ], []);

  const candidateDrillDownColumns = useMemo(() => [
    { key: 'candidateName', label: 'Source Profile', sortable: true },
    { key: 'client', label: 'Client', sortable: true },
    { key: 'role', label: 'Role', sortable: true },
    { key: 'regId', label: 'Req ID', sortable: true },
    { key: 'status', label: 'Status', sortable: true, type: 'status' },
    {
      key: 'requirementId',
      label: 'Action',
      render: (row) => (
        <Link to={`/Requirments?requirementId=${encodeURIComponent(row.requirementId || '')}`}>
          View Requirement
        </Link>
      ),
    },
  ], []);

  const requirementDrillDownColumns = useMemo(() => [
    { key: 'regId', label: 'Req ID', sortable: true },
    { key: 'client', label: 'Client', sortable: true },
    { key: 'role', label: 'Role', sortable: true },
    { key: 'requirementType', label: 'Type', sortable: true },
    {
      key: 'startDate',
      label: 'Start Date',
      sortable: true,
      render: (row) => formatDisplayDate(row.startDate),
    },
    {
      key: 'requirementId',
      label: 'Action',
      render: (row) => (
        <Link to={`/Requirments?requirementId=${encodeURIComponent(row.requirementId || '')}`}>
          Open
        </Link>
      ),
    },
  ], []);

  const drillDownModalColumns = drillDownModal.mode === 'requirement'
    ? requirementDrillDownColumns
    : drillDownModal.mode === 'interview'
      ? interviewColumns
      : candidateDrillDownColumns;

  const drillDownRowKey = drillDownModal.mode === 'requirement'
    ? 'requirementId'
    : 'candidateId';

  return (
    <PageShell
      title="Recruitment Analytics"
      subtitle="Admin analysis dashboard — pipeline funnel and profiles sourced metrics."
      breadcrumbs={[
        { label: 'Dashboard', path: '/dashboard' },
        { label: 'Reports', path: '/reports' },
        { label: 'Recruitment Analytics' },
      ]}
      backTo="/reports"
      actions={(
        <Link to="/Requirments" className="btn btn-outline-secondary">
          Requirement List
        </Link>
      )}
    >
      {loadError && <div className="alert alert-danger">{loadError}</div>}

      {loading ? (
        <p className="hr-empty-state">Loading analytics…</p>
      ) : (
        <>
          <div className="hr-dashboard-date-filter mb-3">
            <RequirementDateRangeFilter
              fromDate={cardStatsFrom}
              toDate={cardStatsTo}
              onFromChange={setCardStatsFrom}
              onToChange={setCardStatsTo}
              defaultFromDate={INITIAL_CARD_STATS_RANGE.from}
              defaultToDate={INITIAL_CARD_STATS_RANGE.to}
              label="Date range"
              className="hr-dashboard-date-filter-fields-wrap"
            />
            <div className="hr-dashboard-date-filter-actions">
              <Button
                variant="primary"
                className="hr-btn-primary hr-dashboard-date-btn"
                size="sm"
                onClick={applyCardStatsFilters}
                disabled={loading}
              >
                Apply
              </Button>
              <Button
                variant="outline-secondary"
                className="hr-dashboard-date-btn"
                size="sm"
                onClick={resetCardStatsFilters}
                disabled={loading}
              >
                Reset
              </Button>
            </div>
          </div>

          <p className="hr-dashboard-filter-hint mb-3">
            Showing:
            {' '}
            {cardStatsPeriod.fromDate || INITIAL_CARD_STATS_RANGE.from}
            {' → '}
            {cardStatsPeriod.toDate || INITIAL_CARD_STATS_RANGE.to}
            {' · '}
            {matchesDefaultDashboardDateRange(
              cardStatsPeriod.fromDate || INITIAL_CARD_STATS_RANGE.from,
              cardStatsPeriod.toDate || INITIAL_CARD_STATS_RANGE.to,
            )
              ? 'Current month to date — '
              : ''}
            requirements by start date, profile metrics by upload date; open requirements are all active reqs
          </p>

          <div className="hr-stat-grid">
            <DashboardCard
              title="Open Reqs"
              value={stats.openRequirements}
              icon="📋"
              accent="orange"
              onClick={() => openDrillDownModal({
                title: 'Open Requirements',
                rows: drillDown.openRequirements,
                mode: 'requirement',
                footerLink: openRequirementsLink,
                footerLabel: 'View in Requirement List',
              })}
            />
            <DashboardCard
              title="Profiles"
              value={stats.profiles}
              icon="👤"
              accent="teal"
              onClick={() => openDrillDownModal({
                title: 'Profiles',
                rows: drillDown.profiles,
                mode: 'candidate',
              })}
            />
            <DashboardCard
              title="Interviews"
              value={interviewCount}
              icon="🎤"
              accent="blue"
              subtitle="Upcoming: Next 7 Days"
              onClick={() => openDrillDownModal({
                title: 'Upcoming Interviews (Next 7 Days)',
                rows: upcomingInterviews,
                mode: 'interview',
              })}
            />
            <DashboardCard
              title="Offers"
              value={stats.offers}
              icon="📨"
              accent="green"
              onClick={() => openDrillDownModal({
                title: 'Offers',
                rows: drillDown.offers,
                mode: 'candidate',
              })}
            />
            <DashboardCard
              title="Offer Accepted"
              value={stats.offerAccept}
              icon="🤝"
              accent="teal"
              onClick={() => openDrillDownModal({
                title: 'Offer Accepted',
                rows: drillDown.offerAccepted,
                mode: 'candidate',
              })}
            />
            <DashboardCard
              title="Joined"
              value={stats.joined ?? 0}
              icon="✅"
              accent="green"
              onClick={() => openDrillDownModal({
                title: 'Joined',
                rows: drillDown.joined,
                mode: 'candidate',
              })}
            />
            <DashboardCard
              title="On Hold"
              value={stats.onHold}
              icon="⏸️"
              accent="orange"
              onClick={() => openDrillDownModal({
                title: 'On Hold Requirements',
                rows: drillDown.onHoldRequirements,
                mode: 'requirement',
                footerLink: onHoldRequirementsLink,
                footerLabel: 'View in Requirement List',
              })}
            />
          </div>

          <div className="hr-analytics-grid">
            <section className="hr-card hr-analytics-panel">
              <h3 className="hr-section-title">Interview Pipeline</h3>
              <p className="hr-dashboard-filter-hint mb-3">
                Data from
                {' '}
                {pipelinePeriod.fromDate || INITIAL_CARD_STATS_RANGE.from}
                {' → '}
                {pipelinePeriod.toDate || INITIAL_CARD_STATS_RANGE.to}
                {' · '}
                Profiles uploaded in this period
              </p>
              <FunnelChart stages={data?.pipelineFunnel || []} />
            </section>

            <section className="hr-card hr-analytics-panel">
              <h3 className="hr-section-title">Upcoming Interviews (Next 7 Days)</h3>
              <DataTable
                columns={interviewColumns}
                data={upcomingInterviews}
                searchPlaceholder="Search interviews"
                searchKeys={['candidateName', 'client', 'role', 'status']}
                emptyMessage="No interviews scheduled in the next 7 days."
                pageSize={5}
              />
            </section>
          </div>

          <section className="hr-card" style={{ marginTop: '20px' }}>
            <h3 className="hr-section-title">Profiles Sourced</h3>
            <p className="hr-page-hint mb-3">
              Count of uploaded candidate profiles by recruiter or team lead, broken down by client and role.
              By default, the last 1 week of uploads is shown.
            </p>

            <div className="hr-employee-work-summary-period-actions mb-3">
              <Button
                type="button"
                variant={profilesPeriod === 'week' ? 'primary' : 'outline-primary'}
                className="hr-employee-work-summary-period-btn"
                onClick={selectProfilesWeek}
                disabled={profilesLoading}
              >
                1 Week
              </Button>
              <Button
                type="button"
                variant={profilesPeriod === 'yesterday' ? 'primary' : 'outline-primary'}
                className="hr-employee-work-summary-period-btn"
                onClick={selectProfilesYesterday}
                disabled={profilesLoading}
              >
                Yesterday
              </Button>
              <Button
                type="button"
                variant={profilesPeriod === 'today' ? 'primary' : 'outline-primary'}
                className="hr-employee-work-summary-period-btn"
                onClick={selectProfilesToday}
                disabled={profilesLoading}
              >
                Today
              </Button>
            </div>

            <section className="hr-card hr-rtl-req-filters-card mb-3">
              <div className="hr-rtl-req-filter-header">
                <svg
                  className="hr-rtl-req-filter-icon"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden
                >
                  <path
                    d="M4 5h16l-6.5 7.5v5.5L10 20v-7.5L4 5z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinejoin="round"
                  />
                </svg>
                <h2 className="hr-rtl-req-filter-title">Filters</h2>
              </div>

              <div className="hr-rtl-req-filter-toolbar hr-rtl-req-profiles-filter-toolbar">
                <div className="hr-rtl-req-filter-group">
                  <span className="hr-rtl-req-filter-label">Date Range</span>
                  <RequirementDateRangeFilter
                    fromDate={fromDate}
                    toDate={toDate}
                    onFromChange={(value) => {
                      setFromDate(value);
                      setProfilesPeriod('');
                    }}
                    onToChange={(value) => {
                      setToDate(value);
                      setProfilesPeriod('');
                    }}
                    inline
                    defaultFromDate={INITIAL_PROFILES_WEEK_RANGE.from}
                    defaultToDate={INITIAL_PROFILES_WEEK_RANGE.to}
                    clearLabel="Clear"
                    onClear={clearProfilesFilters}
                    forceShowClear={Boolean(
                      recruiterId
                      || teamLeadId
                      || profilesPeriod === 'today'
                      || profilesPeriod === 'yesterday'
                      || !matchesDefaultProfilesWeekRange(fromDate, toDate)
                    )}
                    className="hr-rtl-req-date-range"
                  />
                </div>

                <div className="hr-rtl-req-filter-group hr-rtl-req-profiles-roles-group">
                  <div className="hr-rtl-req-profiles-role-fields">
                    <div className="hr-rtl-req-profiles-role-field">
                      <span className="hr-rtl-req-filter-label">Team Lead</span>
                      <select
                        className="form-select hr-rtl-req-filter-input hr-rtl-req-profiles-select"
                        value={teamLeadId}
                        onChange={(event) => {
                          setTeamLeadId(event.target.value);
                          if (event.target.value) setRecruiterId('');
                        }}
                        aria-label="Team Lead filter"
                      >
                        <option value="">All Team Leads</option>
                        {filterOptions.teamLeads.map((teamLead) => (
                          <option key={teamLead.id} value={teamLead.id}>{teamLead.name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="hr-rtl-req-profiles-role-field">
                      <span className="hr-rtl-req-filter-label">Recruiter</span>
                      <select
                        className="form-select hr-rtl-req-filter-input hr-rtl-req-profiles-select"
                        value={recruiterId}
                        onChange={(event) => setRecruiterId(event.target.value)}
                        disabled={Boolean(teamLeadId)}
                        aria-label="Recruiter filter"
                      >
                        <option value="">All Recruiters</option>
                        {filterOptions.recruiters.map((recruiter) => (
                          <option key={recruiter.id} value={recruiter.id}>{recruiter.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                <div className="hr-rtl-req-profiles-filter-actions">
                  <button
                    type="button"
                    className="hr-rtl-req-filter-search-btn"
                    onClick={applyProfilesFilters}
                    disabled={profilesLoading}
                  >
                    Apply Filters
                  </button>
                  <button
                    type="button"
                    className="hr-rtl-req-filter-clear-btn"
                    onClick={clearProfilesFilters}
                    disabled={profilesLoading}
                  >
                    Clear
                  </button>
                </div>
              </div>
            </section>

            {profilesLoading && (
              <p className="hr-page-hint mb-3">Updating profiles sourced report…</p>
            )}

            {!profilesLoading && isDefaultWeekView && (
              <p className="hr-page-hint mb-3">
                Showing default last 1 week data (
                {formatDisplayDate(profilesReport.filters.fromDate)}
                {' – '}
                {formatDisplayDate(profilesReport.filters.toDate)}
                ).
              </p>
            )}

            {!profilesLoading && isTodayProfilesView && (
              <p className="hr-page-hint mb-3">
                Showing profiles sourced today (
                {formatDisplayDate(profilesReport.filters.fromDate)}
                ).
              </p>
            )}

            {!profilesLoading && isYesterdayProfilesView && (
              <p className="hr-page-hint mb-3">
                Showing profiles sourced yesterday (
                {formatDisplayDate(profilesReport.filters.fromDate)}
                ).
              </p>
            )}

            {!profilesLoading && hasProfilesFilters && (
              <p className="hr-page-hint mb-3">
                Total profiles sourced in selected scope: <strong>{profilesReport?.total || 0}</strong>
              </p>
            )}

            <div className="hr-profiles-sourced-charts-grid">
              <ProfilesSourcedBarChartCard
                title="Profiles Sourced by Recruiter & Team Lead"
                items={profilesChartData.byRecruiterAndTeamLead}
              />
              <ProfilesSourcedBarChartCard
                title="Profiles Sourced by Client"
                items={profilesChartData.byClient}
              />
            </div>

            <h4 className="hr-section-title" style={{ fontSize: '1rem', marginBottom: '12px' }}>
              Profiles Sourced Details
            </h4>

            <div className="table-responsive">
                  <table className="table table-bordered table-hover hr-data-table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>User Role</th>
                        <th>Client</th>
                        <th>Role</th>
                        <th>Profiles Sourced</th>
                        <th>Ornnova Screen Selected</th>
                      </tr>
                    </thead>
                    <tbody>
                      {profilesRows.length ? profilesRows.map((row) => (
                        <tr
                          key={row.isTotal ? 'total' : `${row.userId}-${row.client}-${row.requirementRole}`}
                          className={row.isTotal ? 'table-secondary' : undefined}
                        >
                          <td>{row.isTotal ? <strong>{row.name}</strong> : row.name}</td>
                          <td>{row.role || '—'}</td>
                          <td>
                            {!row.isTotal && row.client && row.client !== '—' ? (
                              <Link to={`/Requirments?client=${encodeURIComponent(row.client)}`}>
                                {row.client}
                              </Link>
                            ) : (
                              row.client || '—'
                            )}
                          </td>
                          <td>
                            {!row.isTotal && row.requirementRole && row.requirementRole !== '—' ? (
                              <Link to={`/Requirments?role=${encodeURIComponent(row.requirementRole)}`}>
                                {row.requirementRole}
                              </Link>
                            ) : (
                              row.requirementRole || '—'
                            )}
                          </td>
                          <td><strong>{row.profilesSourced}</strong></td>
                          <td><strong>{row.ornnovaScreenSelected ?? 0}</strong></td>
                        </tr>
                      )) : (
                        <tr>
                          <td colSpan="6">No profiles sourced for the selected filters.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
          </section>
        </>
      )}

      <AppModal
        show={drillDownModal.show}
        onHide={closeDrillDownModal}
        title={drillDownModal.title}
        subtitle={`${drillDownModal.rows.length} record${drillDownModal.rows.length === 1 ? '' : 's'}`}
        size="xl"
        footer={(
          <>
            {drillDownModal.footerLink ? (
              <Link
                to={drillDownModal.footerLink}
                className="btn btn-primary"
                onClick={closeDrillDownModal}
              >
                {drillDownModal.footerLabel}
              </Link>
            ) : null}
            <Button variant="secondary" onClick={closeDrillDownModal}>
              Close
            </Button>
          </>
        )}
      >
        <DataTable
          columns={drillDownModalColumns}
          data={drillDownModal.rows}
          rowKey={drillDownRowKey}
          hideToolbar
          searchPlaceholder={`Search ${drillDownModal.title.toLowerCase()}`}
          searchKeys={
            drillDownModal.mode === 'requirement'
              ? ['regId', 'client', 'role', 'requirementType']
              : drillDownModal.mode === 'interview'
                ? ['candidateName', 'client', 'role', 'status']
                : ['candidateName', 'client', 'role', 'regId', 'status']
          }
          emptyMessage={
            drillDownModal.mode === 'requirement'
              ? 'No active open requirements found.'
              : `No ${drillDownModal.title.toLowerCase()} records for the selected analytics period.`
          }
          pageSize={10}
        />
      </AppModal>
    </PageShell>
  );
}

export default AdminAnalytics;
